import { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/format';
import Attachments from '../components/Attachments';
import ContractorPacket from '../components/ContractorPacket';
import toast from 'react-hot-toast';

const PROD_STATUSES = [
  'Need to Schedule','Schedule Confirmed','On-Site','Clear to Collect',
  'Funding','Funded','Hold','Closed','Job Complete & Funded','Contractor remeasure/Bid','PTD'
];

const STATUS_COLOR = {
  'On-Site': '#1565C0', 'Schedule Confirmed': '#1a355f', 'Need to Schedule': '#E65100',
  'Funded': '#1565C0', 'Closed': '#555', 'Job Complete & Funded': '#1a355f',
  'Clear to Collect': '#F57F17', 'Hold': '#E65100',
};

export default function ContractorPortal() {
  const { username, staff_name } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [activeTab, setActiveTab] = useState({});
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [coForm, setCoForm] = useState({ description: '', amount: '', photos: [] });
  const [submittingCO, setSubmittingCO] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [packetJob, setPacketJob] = useState(null);

  const contractorName = staff_name || username;

  useEffect(() => {
    loadJobs();
    loadMyRequests();
  }, []);

  async function loadJobs() {
    try {
      const res = await api.get('/auth/myjobs');
      setJobs(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error('Failed to load jobs'); }
    finally { setLoading(false); }
  }

  async function loadMyRequests() {
    try {
      const res = await api.get('/change-orders');
      setMyRequests(Array.isArray(res.data) ? res.data : []);
    } catch {}
  }

  async function updateProdStatus(job, newStatus) {
    setUpdatingStatus(job.id);
    try {
      await api.put(`/jobs/${job.id}`, { ...job, prod_status: newStatus });
      setJobs(prev => prev.map(j => j.id === job.id ? { ...j, prod_status: newStatus } : j));
      toast.success('Status updated');
    } catch { toast.error('Failed to update status'); }
    finally { setUpdatingStatus(null); }
  }

  async function submitChangeOrder(job) {
    if (!coForm.description.trim()) return toast.error('Please describe the change order');
    setSubmittingCO(true);
    try {
      const formData = new FormData();
      formData.append('job_id', job.id);
      formData.append('po_number', job.po_number);
      formData.append('description', coForm.description);
      if (coForm.amount) formData.append('amount', coForm.amount);
      coForm.photos.forEach(f => formData.append('photos', f));
      await api.post('/change-orders', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Change order request submitted!');
      setCoForm({ description: '', amount: '', photos: [] });
      loadMyRequests();
    } catch { toast.error('Failed to submit change order'); }
    finally { setSubmittingCO(false); }
  }

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  return (
    <>
      <div className="page-header">
        <h2>My Assigned Jobs</h2>
        <div style={{ fontSize: 13, color: '#555' }}>
          <strong>{contractorName}</strong> · {jobs.length} jobs assigned
        </div>
      </div>

      <div className="page-body">
        {/* Summary */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <SummaryCard label="Assigned Jobs" value={jobs.length} color="#0d1f3b" />
          <SummaryCard label="Pending Change Orders" value={myRequests.filter(r => r.status === 'Pending').length} color="#F57F17" />
          <SummaryCard label="Approved Change Orders" value={myRequests.filter(r => r.status === 'Approved').length} color="#1a355f" />
        </div>

        {jobs.length === 0 ? (
          <div className="empty-state"><p>No jobs assigned to {contractorName}</p></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {jobs.map(job => {
              const isOpen = expanded === job.id;
              const tab = activeTab[job.id] || 'details';
              const services = [
                job.con_roof === contractorName ? 'Roofing' : null,
                job.con_ec === contractorName ? 'Ext Coating' : null,
                job.con_win === contractorName ? 'Windows' : null,
                job.con_gut === contractorName ? 'Gutters' : null,
              ].filter(Boolean);

              return (
                <div key={job.id} style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                  {/* Header */}
                  <div onClick={() => setExpanded(isOpen ? null : job.id)}
                    style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: '#0d1f3b' }}>{job.customer_name}</div>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{job.po_number} · {job.address || ''} {job.city || ''}</div>
                      <div style={{ fontSize: 12, color: '#1565C0', marginTop: 2 }}>{services.join(', ')}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <button className='btn btn-outline btn-sm' onClick={e => { e.stopPropagation(); setPacketJob(job); }}>📋 Packet</button>
                      <StatusBadge label={job.prod_status || 'No Status'} color={STATUS_COLOR[job.prod_status] || '#888'} />
                      <div style={{ color: '#aaa', fontSize: 18 }}>{isOpen ? '▲' : '▼'}</div>
                    </div>
                  </div>

                  {/* Expanded */}
                  {isOpen && (
                    <div style={{ borderTop: '1px solid #f0f0f0' }}>
                      {/* Tabs */}
                      <div style={{ display: 'flex', borderBottom: '1px solid #eee', background: '#fafafa' }}>
                        {['details', 'status', 'attachments', 'change-order'].map(t => (
                          <button key={t} onClick={() => setActiveTab(prev => ({ ...prev, [job.id]: t }))}
                            style={{ padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: tab === t ? 700 : 400,
                              color: tab === t ? '#0d1f3b' : '#888', borderBottom: tab === t ? '2px solid #0d1f3b' : '2px solid transparent', fontSize: 13 }}>
                            {t === 'change-order' ? '+ Change Order' : t.charAt(0).toUpperCase() + t.slice(1)}
                          </button>
                        ))}
                      </div>

                      <div style={{ padding: '16px 18px' }}>
                        {/* Details tab */}
                        {tab === 'details' && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                            <div>
                              <SectionTitle>Job Info</SectionTitle>
                              <InfoRow label="Customer" value={job.customer_name} />
                              <InfoRow label="Address" value={job.address || '—'} />
                              <InfoRow label="City" value={job.city || '—'} />
                              <InfoRow label="Phone" value={job.phone || '—'} />
                              <InfoRow label="Start Date" value={job.start_date || '—'} />
                              <InfoRow label="End Date" value={job.end_date || '—'} />
                            </div>
                            <div>
                              <SectionTitle>Scope & Materials</SectionTitle>
                              <div style={{ fontSize: 13, color: '#333', marginBottom: 8 }}>
                                <strong>Scope:</strong> {job.scope || 'Not specified'}
                              </div>
                              <div style={{ fontSize: 13, color: '#333', marginBottom: 8 }}>
                                <strong>Color:</strong> {job.color || 'Not specified'}
                              </div>
                              <div style={{ fontSize: 13, color: '#333', marginBottom: 8 }}>
                                <strong>Materials:</strong> {job.materials || 'Not specified'}
                              </div>
                              <div style={{ fontSize: 13, color: '#333' }}>
                                <strong>Permit:</strong> {job.permit || 'Not specified'}
                              </div>
                            </div>
                            <div>
                              <SectionTitle>Services Assigned</SectionTitle>
                              {services.map(s => (
                                <div key={s} style={{ padding: '6px 10px', background: '#e8edf5', borderRadius: 6, marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#1a355f' }}>✓ {s}</div>
                              ))}
                              {job.notes && (
                                <div style={{ marginTop: 12, padding: '10px 12px', background: '#fff8e1', borderRadius: 6, fontSize: 13 }}>
                                  <strong>Notes:</strong> {job.notes}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Status tab */}
                        {tab === 'status' && (
                          <div style={{ maxWidth: 400 }}>
                            <SectionTitle>Update Production Status</SectionTitle>
                            <div style={{ marginBottom: 12, padding: '10px 14px', background: '#f5f5f5', borderRadius: 8 }}>
                              Current: <strong>{job.prod_status || 'Not set'}</strong>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {PROD_STATUSES.map(s => (
                                <button key={s} onClick={() => updateProdStatus(job, s)}
                                  disabled={updatingStatus === job.id || job.prod_status === s}
                                  style={{
                                    padding: '10px 14px', borderRadius: 8, border: '1px solid',
                                    borderColor: job.prod_status === s ? '#1a355f' : '#ddd',
                                    background: job.prod_status === s ? '#e8edf5' : '#fff',
                                    color: job.prod_status === s ? '#1a355f' : '#333',
                                    fontWeight: job.prod_status === s ? 700 : 400,
                                    cursor: job.prod_status === s ? 'default' : 'pointer',
                                    textAlign: 'left', fontSize: 13
                                  }}>
                                  {job.prod_status === s ? '✓ ' : ''}{s}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Attachments tab */}
                        {tab === 'attachments' && (
                          <div>
                            <SectionTitle>Job Attachments & Photos</SectionTitle>
                            <Attachments jobId={job.id} />
                          </div>
                        )}

                        {/* Change order tab */}
                        {tab === 'change-order' && (
                          <div style={{ maxWidth: 500 }}>
                            <SectionTitle>Request a Change Order</SectionTitle>
                            <p style={{ fontSize: 13, color: '#777', marginBottom: 16 }}>
                              Describe additional work needed that was not in the original contract. Admin will review and approve.
                            </p>

                            <div className="form-group" style={{ marginBottom: 14 }}>
                              <label className="form-label">Description of Additional Work *</label>
                              <textarea className="form-textarea" rows={4} placeholder="Describe what additional work is needed and why..."
                                value={coForm.description} onChange={e => setCoForm(f => ({ ...f, description: e.target.value }))} />
                            </div>

                            <div className="form-group" style={{ marginBottom: 14 }}>
                              <label className="form-label">Estimated Amount ($)</label>
                              <input className="form-input" type="number" placeholder="0"
                                value={coForm.amount} onChange={e => setCoForm(f => ({ ...f, amount: e.target.value }))} />
                            </div>

                            <div className="form-group" style={{ marginBottom: 16 }}>
                              <label className="form-label">Attach Photos</label>
                              <input type="file" multiple accept="image/*"
                                onChange={e => setCoForm(f => ({ ...f, photos: Array.from(e.target.files) }))}
                                style={{ fontSize: 13 }} />
                              {coForm.photos.length > 0 && <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{coForm.photos.length} photo(s) selected</div>}
                            </div>

                            <button className="btn btn-primary" onClick={() => submitChangeOrder(job)} disabled={submittingCO}>
                              {submittingCO ? 'Submitting…' : 'Submit Change Order Request'}
                            </button>

                            {/* Past requests for this job */}
                            {myRequests.filter(r => r.job_id == job.id).length > 0 && (
                              <div style={{ marginTop: 20 }}>
                                <SectionTitle>Previous Requests</SectionTitle>
                                {myRequests.filter(r => r.job_id == job.id).map(r => (
                                  <div key={r.id} style={{ padding: '10px 14px', background: '#f9f9f9', borderRadius: 8, marginBottom: 8, borderLeft: '3px solid ' + (r.status === 'Approved' ? '#1a355f' : r.status === 'Denied' ? '#c62828' : '#F57F17') }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                      <span style={{ fontWeight: 600, fontSize: 13 }}>{r.description.slice(0, 60)}...</span>
                                      <StatusBadge label={r.status} color={r.status === 'Approved' ? '#1a355f' : r.status === 'Denied' ? '#c62828' : '#F57F17'} />
                                    </div>
                                    {r.amount && <div style={{ fontSize: 12, color: '#555' }}>Requested: {formatCurrency(r.amount)}</div>}
                                    {r.admin_notes && <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>Admin: {r.admin_notes}</div>}
                                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>{r.created_at}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

function StatusBadge({ label, color }) {
  return <span style={{ background: color+'22', color, border: '1px solid '+color+'44', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{label}</span>;
}
function SummaryCard({ label, value, color }) {
  return (
    <div style={{ background: '#fff', border: '2px solid '+color, borderRadius: 8, padding: '10px 18px', minWidth: 140 }}>
      <div style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}
function SectionTitle({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{children}</div>;
}
function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, borderBottom: '1px solid #f5f5f5' }}>
      <span style={{ color: '#888' }}>{label}</span>
      <span style={{ fontWeight: 600, color: '#333' }}>{value}</span>
    </div>
  );
}
